import React, { useState, useEffect, useRef, useMemo } from 'react';
import { IoClose } from 'react-icons/io5';
import { PALETTES } from '../utils/colorUtils';
import useConfig from '../config';
import { parseCsvData } from '../utils/csvParser';
import { useCampus } from '../contexts/CampusContext';
import { CourseData, searchCourses } from '../utils/courseSearch';
import { hasConflictWithSlots } from '../utils/time';

// Utility function to check if a slot is a theory slot
const isTheorySlot = (slot: string): boolean => {
  // Theory slots either start with A-G or T followed by A-G
  return /^[A-G][12]$/.test(slot) || /^T[A-G]{1,2}[12]$/.test(slot);
};

// Filter courses to only include those with theory slots
// const COURSE_DATA = parseCsvData(velloreData).map((course: any) => ({
//   name: course.TITLE,
//   code: course.CODE,
//   slots: course.SLOT.split('+'),
//   credits: parseFloat(course.CREDITS) || 0,
//   faculty: course.FACULTY,
//   venue: course.VENUE
// })).filter(course => course.slots.some(isTheorySlot)).map(course => ({
//   ...course,
//   slots: course.slots.filter(isTheorySlot)
// })).filter(course => course.slots.length > 0);

// MOCK DATA for course search
// const MOCK_COURSES = [
//   { name: 'Introduction to Programming', slots: ['A1', 'TC1'], credits: 4 },
//   { name: 'Data Structures', slots: ['B2', 'TD2'], credits: 4 },
//   { name: 'Data Structures', slots: ['C1', 'TE1'], credits: 4 },
//   { name: 'Database Management Systems', slots: ['D1', 'TF1'], credits: 4 },
//   { name: 'Operating Systems', slots: ['E2', 'TG2'], credits: 3 },
//   { name: 'Computer Networks', slots: ['F1', 'TAA1'], credits: 3 },
//   { name: 'Software Engineering', slots: ['G2', 'TBB2'], credits: 4 },
// ];

// Define slot conflicts here or import from a shared utility
// const slotConflictPairs = [
// ... existing code ...
//   ['E2', 'L55'], ['C2', 'L56'], ['TA2', 'L57'], ['TF2', 'L58'], ['TDD2', 'L59']
// ];

// const slotConflictsMap = new Map<string, string[]>();
// slotConflictPairs.forEach(([slot1, slot2]) => {
//   const slot1Conflicts = slotConflictsMap.get(slot1) || [];
//   slotConflictsMap.set(slot1, [...slot1Conflicts, slot2]);
//   const slot2Conflicts = slotConflictsMap.get(slot2) || [];
//   slotConflictsMap.set(slot2, [...slot2Conflicts, slot1]);
// });

// --- Slot Association Logic (Module Level) ---
// const morningTheorySlotsConst = ['A1', 'B1', 'C1', 'D1', 'E1', 'F1', 'G1', 'TA1', 'TB1', 'TC1', 'TD1', 'TE1', 'TF1', 'TG1', 'TAA1', 'TCC1'];
// const eveningTheorySlotsConst = ['A2', 'B2', 'C2', 'D2', 'E2', 'F2', 'G2', 'TA2', 'TB2', 'TC2', 'TD2', 'TE2', 'TF2', 'TAA2', 'TBB2', 'TCC2', 'TDD2'];

const getAssociatedSlots = (primarySlot: string, availableTheorySlots: string[]): string[] => {
  const associates: string[] = [];
  if (primarySlot.length < 2 || primarySlot.length > 3) return associates;

  const baseLetter = primarySlot[0];
  if (!/^[A-G]$/.test(baseLetter)) return associates;

  const firstAssociatePattern = `T${primarySlot}`;
  if (availableTheorySlots.includes(firstAssociatePattern)) {
    associates.push(firstAssociatePattern);
  }

  const secondAssociatePattern = `T${baseLetter}${primarySlot}`;
  if (availableTheorySlots.includes(secondAssociatePattern)) {
    if (!associates.includes(secondAssociatePattern)) {
      associates.push(secondAssociatePattern);
    }
  }
  return associates;
};

const sortSlots = (slots: string[]): string[] => {
  const getPrefixLength = (slot: string): number => {
    const match = slot.match(/[a-zA-Z]+/);
    return match ? match[0].length : 0;
  };

  return [...slots].sort((a, b) => {
    const lenA = getPrefixLength(a);
    const lenB = getPrefixLength(b);

    if (lenA !== lenB) {
      return lenA - lenB;
    }

    return a.localeCompare(b);
  });
};

interface CourseSlotSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    courseName: string;
    selectedSlots: string[];
    credits: number;
    facultyPreferences?: string[];
    includeLabCourse?: boolean;
    facultyLabAssignments?: Map<string, string[]>;
  }) => void;
  onAddFaculty: (data: {
    courseName: string;
    selectedSlots: string[];
    credits: number;
    colorIndex: number;
    hasLabComponent: boolean;
    isSlashCommandSelection: boolean;
  }) => void;
  existingSlots?: string[]; // Add this prop to track already selected slots
  editingCourse?: { name: string; slots: string[]; credits: number; facultyPreferences?: string[]; facultyLabAssignments?: Array<{ facultyName: string; slots: string[] }>; colorIndex: number }; // Add prop for editing
  referenceElement?: HTMLElement;
  placement?: 'bottom' | 'top' | 'left' | 'right';
  isMorningTheory?: boolean;
  isFirstFaculty?: boolean;
  blockedSlots?: string[];
  preSelectedSlots?: string[];
  palette: keyof typeof PALETTES;
  colorIndex: number;
}

const CourseSlotSelector: React.FC<CourseSlotSelectorProps> = ({
  isOpen,
  onClose,
  onSubmit,
  onAddFaculty,
  existingSlots = [],
  editingCourse = undefined,
  referenceElement,
  placement = 'bottom',
  isMorningTheory = false,
  isFirstFaculty = false,
  blockedSlots = [],
  preSelectedSlots = [],
  palette,
  colorIndex
}) => {
  const config = useConfig();
  const { morningTheorySlotsConst, eveningTheorySlotsConst, slotConflictPairs: configSlotConflictPairs } = config;
  const { campus } = useCampus();

  const [courseData, setCourseData] = useState<CourseData[]>([]);
  const [allCourses, setAllCourses] = useState<any[]>([]); // To store all courses before filtering
  const [isLoading, setIsLoading] = useState(true);
  const [courseName, setCourseName] = useState('');
  const [selectedSlots, setSelectedSlots] = useState<string[]>([]);
  const [credits, setCredits] = useState(0);
  const [activeTimingTab, setActiveTimingTab] = useState<'morning' | 'evening'>('morning');
  const [creditInputString, setCreditInputString] = useState('0');
  const [searchResults, setSearchResults] = useState<CourseData[]>([]);
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const [hasLabComponent, setHasLabComponent] = useState(false);
  const [isSlashCommandSelection, setIsSlashCommandSelection] = useState(false);
  const [shakeCourseName, setShakeCourseName] = useState(false);
  const [shakeSlots, setShakeSlots] = useState(false);
  const [focusedSearchIndex, setFocusedSearchIndex] = useState(-1);
  const searchResultsRef = useRef<HTMLDivElement>(null);
  const searchDropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const resetForm = () => {
    setCourseName('');
    setSelectedSlots([]);
    setCredits(0);
    setCreditInputString('0');
    setActiveTimingTab('morning');
    setHasLabComponent(false);
    setIsSlashCommandSelection(false);
  };

  const validateAndSubmit = (action: 'addFaculty' | 'skipFaculty' | 'confirmEdit') => {
    const isCourseNameEmpty = !courseName.trim();
    const areSlotsEmpty = selectedSlots.length === 0;

    if (isCourseNameEmpty || areSlotsEmpty) {
      if (isCourseNameEmpty) {
        setShakeCourseName(true);
        setTimeout(() => setShakeCourseName(false), 820);
      }
      if (areSlotsEmpty) {
        setShakeSlots(true);
        setTimeout(() => setShakeSlots(false), 820);
      }
      return;
    }

    const submissionData = {
      courseName,
      selectedSlots,
      credits,
      facultyPreferences: action === 'skipFaculty' ? [] : (editingCourse?.facultyPreferences || []),
      includeLabCourse: action === 'confirmEdit' ? !!editingCourse?.facultyLabAssignments : undefined,
      facultyLabAssignments: action === 'confirmEdit' && editingCourse?.facultyLabAssignments 
        ? new Map(editingCourse.facultyLabAssignments.map(a => [a.facultyName, a.slots]))
        : undefined,
    };

    if (action === 'addFaculty') {
      onAddFaculty({
        courseName,
        selectedSlots,
        credits,
        colorIndex: colorIndex,
        hasLabComponent,
        isSlashCommandSelection
      });
    } else {
      onSubmit(submissionData);
      onClose();
    }
  };

  const slotConflictsMap = new Map<string, string[]>();
  configSlotConflictPairs.forEach(([slot1, slot2]) => {
    const slot1Conflicts = slotConflictsMap.get(slot1) || [];
    slotConflictsMap.set(slot1, [...slot1Conflicts, slot2]);
    const slot2Conflicts = slotConflictsMap.get(slot2) || [];
    slotConflictsMap.set(slot2, [...slot2Conflicts, slot1]);
  });

  const morningSlotAssociations = new Map<string, string[]>();
  const primaryMorningSlotsList = ['A1', 'B1', 'C1', 'D1', 'E1', 'F1', 'G1'];
  primaryMorningSlotsList.forEach(ps => {
    morningSlotAssociations.set(ps, getAssociatedSlots(ps, morningTheorySlotsConst));
  });

  const eveningSlotAssociations = new Map<string, string[]>();
  const primaryEveningSlotsList = ['A2', 'B2', 'C2', 'D2', 'E2', 'F2', 'G2'];
  primaryEveningSlotsList.forEach(ps => {
    eveningSlotAssociations.set(ps, getAssociatedSlots(ps, eveningTheorySlotsConst));
  });

  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);
  const [arrowElement, setArrowElement] = useState<HTMLDivElement | null>(null);
  const courseNameInputRef = useRef<HTMLInputElement>(null);

  // Use the constants for slot patterns
  const morningTheorySlots = sortSlots(morningTheorySlotsConst);
  const eveningTheorySlots = sortSlots(eveningTheorySlotsConst);

  // Fetch and parse CSV data
  useEffect(() => {
    const fetchData = async () => {
      try {
        let dataPath;
        console.log(`[CourseSlotSelector] Fetching data for campus: ${campus}`);
        switch (campus) {
          case 'Chennai':
            dataPath = await import('../data/report_chennai.csv');
            console.log('[CourseSlotSelector] Importing Chennai data');
            break;
          case 'AP':
            dataPath = await import('../data/report_ap.csv');
            console.log('[CourseSlotSelector] Importing AP data');
            break;
          default:
            dataPath = await import('../data/report_vellore.csv');
            console.log('[CourseSlotSelector] Importing Vellore data');
        }

        const response = await fetch(dataPath.default);
        const text = await response.text();
        console.log(`[CourseSlotSelector] CSV text for ${campus}:`, text.substring(0, 200) + '...');
        if (text.trim() === '') {
            setCourseData([]);
            setAllCourses([]);
            setIsLoading(false);
            return;
        }
        const parsedData = parseCsvData(text) as any[];
        console.log(`[CourseSlotSelector] Parsed data for ${campus}:`, parsedData.slice(0, 5));
        
        const allMappedCourses = parsedData.map(course => ({
            name: course.TITLE,
            code: course.CODE,
            slots: course.SLOT.split('+'),
            credits: parseFloat(course.CREDITS) || 0,
            faculty: course.FACULTY,
            venue: course.VENUE,
            type: course.TYPE
        }));
        setAllCourses(allMappedCourses); // Store all courses

        // Transform and filter the data
        const transformedData = allMappedCourses
          .filter(course => course.slots.some(isTheorySlot))
          .map(course => ({
            ...course,
            slots: course.slots.filter(isTheorySlot)
          }))
          .filter(course => course.slots.length > 0);

        // Remove duplicates based on course name and slots
        const uniqueCourses = transformedData.reduce((acc, current) => {
          const key = `${current.name}-${current.slots.sort().join('+')}`;
          if (!acc.some(course => 
            course.name === current.name && 
            course.slots.length === current.slots.length && 
            course.slots.every(slot => current.slots.includes(slot))
          )) {
            acc.push(current);
          }
          return acc;
        }, [] as CourseData[]);

        setCourseData(uniqueCourses);
        console.log(`[CourseSlotSelector] Final unique course data for ${campus}:`, uniqueCourses.slice(0, 5));
        setIsLoading(false);
      } catch (error) {
        console.error('Error loading course data:', error);
        setIsLoading(false);
      }
    };

    fetchData();
  }, [campus]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      courseNameInputRef.current?.focus();
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      if (editingCourse) {
        setCourseName(editingCourse.name);
        setSelectedSlots(editingCourse.slots);
        setCredits(editingCourse.credits);
        setCreditInputString(editingCourse.credits.toString());
      } else {
        resetForm();
      }
    }
  }, [isOpen, editingCourse]);

  useEffect(() => {
    if (!isSearchVisible) {
      setFocusedSearchIndex(-1);
    }
  }, [isSearchVisible]);

  useEffect(() => {
    if (focusedSearchIndex >= 0 && searchResultsRef.current) {
      const searchResults = searchResultsRef.current;
      const focusedElement = searchResults.children[focusedSearchIndex] as HTMLElement;
      if (focusedElement) {
        focusedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [focusedSearchIndex]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchDropdownRef.current && 
        !searchDropdownRef.current.contains(event.target as Node) &&
        !searchInputRef.current?.contains(event.target as Node)
      ) {
        setIsSearchVisible(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (isSearchVisible) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusedSearchIndex(prev => 
          prev < searchResults.length - 1 ? prev + 1 : prev
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusedSearchIndex(prev => prev > -1 ? prev - 1 : prev);
      } else if (e.key === 'Enter' && focusedSearchIndex !== -1) {
        e.preventDefault();
        handleSelectCourse(searchResults[focusedSearchIndex]);
        setFocusedSearchIndex(-1);
      }
    }
  };

  const handleCloseModal = () => {
    // Just close without saving any changes
    resetForm();
    onClose();
  };

  const handleCourseNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCourseName(value);

    if (value.startsWith('/')) {
      setIsSearchVisible(true);
      const query = value.substring(1);
      const filteredCourses = searchCourses(query, courseData, isSlotTaken);
      setSearchResults(filteredCourses);
    } else {
      setIsSearchVisible(false);
      setIsSlashCommandSelection(false);
      // For manually entered courses, always allow adding a lab in the next step.
      setHasLabComponent(true);
    }
  };

  const handleSelectCourse = (course: { name: string; slots: string[]; credits: number; code?: string; faculty?: string; type?: string; }) => {
    setCourseName(course.name);
    
    const newCredits = course.credits || 0;
    setCredits(newCredits);
    setCreditInputString(newCredits.toString());
    
    setIsSearchVisible(false);

    // Check if the course has a lab component
    const labCourseName = `${course.name.trim()} LAB`;
    const hasSeparateLabCourse = allCourses.some(c => c.name.trim().toUpperCase() === labCourseName.toUpperCase());
    const isEmbedded = course.type === 'ETH' || course.type === 'ELA';
    setHasLabComponent(hasSeparateLabCourse || isEmbedded);
    setIsSlashCommandSelection(true);

    // Determine which slots belong to morning and evening
    const morningSlots = course.slots.filter(slot => morningTheorySlots.includes(slot));
    const eveningSlots = course.slots.filter(slot => eveningTheorySlots.includes(slot));

    // Set all slots first
    setSelectedSlots(course.slots);

    // Set the tab based on which has more slots, or keep current if equal
    if (morningSlots.length > eveningSlots.length) {
      setActiveTimingTab('morning');
    } else if (eveningSlots.length > morningSlots.length) {
      setActiveTimingTab('evening');
    }

    // Focus out of the input to dismiss keyboard on mobile
    courseNameInputRef.current?.blur();
  };

  const toggleSlot = (slot: string) => {
    if (isSlotTaken(slot) && !(editingCourse && editingCourse.slots.includes(slot))) return;
    
    if (selectedSlots.includes(slot)) {
      // If the slot is already selected, remove it.
      setSelectedSlots(selectedSlots.filter(s => s !== slot));
    } else {
      // If the slot is not selected, add it.
      setSelectedSlots([...selectedSlots, slot]);
    }
  };

  const isSlotTaken = (slot: string): boolean => {
    if (editingCourse && editingCourse.slots.includes(slot)) {
      return false;
    }
    if (existingSlots.includes(slot)) {
      return true;
    }
    
    // Use time-based conflict detection
    if (hasConflictWithSlots(slot, existingSlots, config)) {
      if (editingCourse && editingCourse.slots.includes(slot)) {
        // This slot is part of the course being edited, not an external block
        return false;
      }
      return true;
    }

    // Check conflicts with currently selected slots
    if (hasConflictWithSlots(slot, selectedSlots, config)) {
      if (editingCourse && editingCourse.slots.includes(slot) && editingCourse.slots.includes(slot)) {
        // This specific conflict was part of the original course
        return false;
      }
      return true;
    }

    return false;
  };

  const handleCourseDetailSubmit = () => {
    if (courseName && selectedSlots.length > 0) {
      onAddFaculty({
        courseName,
        selectedSlots,
        credits,
        colorIndex: colorIndex,
        hasLabComponent,
        isSlashCommandSelection
      });
    } else {
      setShakeCourseName(!courseName);
      setShakeSlots(selectedSlots.length === 0);
      setTimeout(() => {
        setShakeCourseName(false);
        setShakeSlots(false);
      }, 820);
    }
  };

  const handleSkipFaculty = () => {
    if (courseName && selectedSlots.length > 0) {
      onSubmit({
        courseName,
        selectedSlots,
        credits,
        facultyPreferences: [], // No faculty preferences
      });
      onClose(); // This is crucial to close the modal
    } else {
      setShakeCourseName(!courseName);
      setShakeSlots(selectedSlots.length === 0);
      setTimeout(() => {
        setShakeCourseName(false);
        setShakeSlots(false);
      }, 820);
    }
  };

  const handleConfirmEdit = () => {
    if (courseName && selectedSlots.length > 0) {
      onSubmit({
        courseName,
        selectedSlots,
        credits,
        // When just confirming an edit from this modal, we assume no faculty change.
        // Faculty changes are handled through the "Edit Faculty" button on the card.
        facultyPreferences: editingCourse?.facultyPreferences || [],
        includeLabCourse: !!editingCourse?.facultyLabAssignments, 
        facultyLabAssignments: editingCourse?.facultyLabAssignments 
          ? new Map(editingCourse.facultyLabAssignments.map(a => [a.facultyName, a.slots]))
          : undefined,
      });
      onClose();
    } else {
      setShakeCourseName(!courseName);
      setShakeSlots(selectedSlots.length === 0);
      setTimeout(() => {
        setShakeCourseName(false);
        setShakeSlots(false);
      }, 820);
    }
  };

  // Get slot rows based on the active timing tab
  const getSlotRows = () => {
    const slots = activeTimingTab === 'evening' ? eveningTheorySlots : morningTheorySlots;
    
    // Create rows of 4 slots each for consistent layout
    const rows = [];
    for (let i = 0; i < slots.length; i += 4) {
      rows.push(slots.slice(i, i + 4));
    }
    return rows;
  };

  const isLabAssociated = () => {
    // Implement the logic to determine if a lab course is associated
    return false; // Placeholder return, actual implementation needed
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 flex items-center justify-center z-50">
        <div className="fixed inset-0 bg-gray-900 bg-opacity-40 backdrop-blur-sm"></div>
        <div className="bg-white rounded-2xl p-6 w-[800px] z-10 relative">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-semibold text-gray-900 break-words max-w-[80%]">
              {editingCourse ? 'Edit Course Details' : 'Enter Course Details'}
            </h2>
            <button 
              onClick={handleCloseModal}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <IoClose size={24} />
            </button>
          </div>

          {/* Course Name and Credits Input */}
          <div className="flex gap-4 mb-6">
            <div className={`flex-1 relative ${shakeCourseName ? 'animate-shake' : ''}`}>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Course Name
              </label>
              <input
                type="text"
                ref={searchInputRef}
                value={courseName}
                maxLength={34}
                onChange={handleCourseNameChange}
                onKeyDown={handleKeyDown}
                className="w-full px-6 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-0 focus:border-black transition-all text-base break-words"
                placeholder="use '/' to search or Enter course name"
              />
              {isSearchVisible && (
                <div 
                  ref={searchDropdownRef}
                  className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-20 max-h-[200px] overflow-y-auto"
                >
                  {isLoading ? (
                    <div className="px-4 py-2 text-gray-500">Loading courses...</div>
                  ) : searchResults.length > 0 ? (
                    searchResults.map((course, index) => {
                      const isFocused = index === focusedSearchIndex;
                      return (
                        <button
                          key={index}
                          onClick={() => handleSelectCourse(course)}
                          className={`w-full text-left hover:bg-gray-100 ${isFocused ? 'bg-gray-100' : ''}`}
                        >
                          <div className="px-4 py-2 border-b border-gray-100">
                            <div className="flex items-center justify-between mb-1.5">
                              <span className="text-gray-900">{course.name}</span>
                            </div>
                            <div className="flex gap-1.5">
                              <span className="bg-gray-700 text-white px-2 py-0.5 text-xs font-mono rounded">
                                {course.slots.join('+')}
                              </span>
                            </div>
                          </div>
                        </button>
                      );
                    })
                  ) : (
                    <div className="px-4 py-2 text-gray-500">No courses found</div>
                  )}
                </div>
              )}
            </div>
            
            <div className="w-32">
              <label htmlFor="credits-input" className="block text-sm font-medium text-gray-700 mb-2">
                Credits
              </label>
              <div className="relative flex items-center">
                <input
                  id="credits-input"
                  type="text"
                  value={creditInputString}
                  maxLength={4}
                  onChange={(e) => {
                    const val = e.target.value;
                    // Allow only numbers and a single decimal point
                    if (!/^[0-9]*\.?[0-9]*$/.test(val)) return;
                    setCreditInputString(val);
                    if (val === '') {
                      setCredits(0); 
                    } else {
                      const num = parseFloat(val);
                      if (!isNaN(num) && num >= 0 && num <= 99) {
                        setCredits(num);
                      }
                    }
                  }}
                  onBlur={(e) => {
                    const val = e.target.value;
                    if (val === '' || val === '.') {
                      setCreditInputString('0');
                      setCredits(0);
                    } else {
                      const num = parseFloat(val);
                      if (!isNaN(num) && num >= 0 && num <= 99) {
                        // Format to at most 1 decimal place
                        const formattedNum = Math.round(num * 10) / 10;
                        setCreditInputString(formattedNum.toString());
                        setCredits(formattedNum);
                      } else {
                        setCreditInputString(credits.toString());
                      }
                    }
                  }}
                  className="w-full text-center border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:ring-0 focus:border-black pr-10 text-base"
                  aria-label="Credits value"
                />
                <div className="absolute right-0 top-0 bottom-0 flex flex-col items-center justify-center pr-1">
                  <button
                    type="button"
                    onClick={() => {
                      const newCredits = Math.min(99, credits + 0.5);
                      setCredits(newCredits);
                      setCreditInputString(newCredits.toString());
                    }}
                    className="h-1/2 px-1 text-gray-500 hover:text-gray-700 flex items-center justify-center rounded-tr-md focus:outline-none"
                    aria-label="Increase credits"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 15l7-7 7 7" /></svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const newCredits = Math.max(0, credits - 0.5);
                      setCredits(newCredits);
                      setCreditInputString(newCredits.toString());
                    }}
                    className="h-1/2 px-1 text-gray-500 hover:text-gray-700 flex items-center justify-center rounded-br-md focus:outline-none"
                    aria-label="Decrease credits"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M19 9l-7 7-7-7" /></svg>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Slots Grid */}
          <div className={shakeSlots ? 'animate-shake' : ''}>
            {/* Timing Tabs */}
            <div className="flex mb-4 border-b border-gray-200">
              <button
                onClick={() => setActiveTimingTab('morning')}
                className={`px-4 py-2 text-sm font-medium transition-colors
                  ${activeTimingTab === 'morning'
                    ? 'border-b-2 border-black text-black'
                    : 'text-gray-500 hover:text-gray-700'
                  }
                `}
              >
                Morning Slots
              </button>
              <button
                onClick={() => setActiveTimingTab('evening')}
                className={`px-4 py-2 text-sm font-medium transition-colors
                  ${activeTimingTab === 'evening'
                    ? 'border-b-2 border-black text-black'
                    : 'text-gray-500 hover:text-gray-700'
                  }
                `}
              >
                Evening Slots
              </button>
            </div>

            {/* Show slots based on active tab but keep all slots selected */}
            {getSlotRows().map((row, rowIndex) => (
              <div key={rowIndex} className="flex gap-2 mb-2">
                {row.map(slot => {
                  const isVisible = activeTimingTab === 'morning' 
                    ? morningTheorySlots.includes(slot)
                    : eveningTheorySlots.includes(slot);
                  
                  if (!isVisible) return null;

                  return (
                  <button
                    key={slot}
                    onClick={() => toggleSlot(slot)}
                    disabled={isSlotTaken(slot) && !selectedSlots.includes(slot)}
                    className={`
                      flex-1 p-3 rounded-lg text-sm font-medium transition-all
                      ${selectedSlots.includes(slot)
                        ? 'bg-black text-white border border-black'
                        : isSlotTaken(slot)
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-70'
                          : 'bg-gray-200 text-gray-700 border border-gray-200'
                      }
                    `}
                  >
                    {slot}
                  </button>
                  );
                })}
              </div>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 mt-6">
            {editingCourse ? (
              // Show only confirm/cancel when editing
              <>
                <button
                  onClick={handleConfirmEdit}
                  className={`
                    px-6 py-3 rounded-lg text-sm font-medium text-white transition-colors
                    ${courseName && selectedSlots.length > 0
                      ? 'bg-black hover:bg-gray-900'
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-70'
                    }
                  `}
                >
                  Confirm Changes
                </button>
              </>
            ) : (
              // Show regular options for new course creation
              <>
                <button
                  onClick={handleCourseDetailSubmit}
                  className={`
                    px-6 py-3 rounded-lg text-sm font-medium text-white transition-colors
                    ${courseName && selectedSlots.length > 0
                      ? 'bg-black hover:bg-gray-900'
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-70'
                    }
                  `}
                >
                  Next: Add Faculty
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default CourseSlotSelector; 